/**
 * Orders of the task report, sent to `GET task-hist` as `sortBy`.
 * The API sorts and paginates the whole list; the client keeps the same order
 * when it merges a page into the cards already shown.
 */
export enum TaskHistSortOrder {
  /** Work date (`metadata.endTimestamp`), newest first. The default. */
  WorkDate = 'workDate',
  /** Upload date of the logged-in user, newest first. */
  UploadDate = 'uploadDate',
  /** File name of the logged-in user, A to Z, case-insensitive. */
  FileName = 'fileName',
}

export const DEFAULT_TASK_HIST_SORT_ORDER = TaskHistSortOrder.WorkDate;

export const TASK_HIST_SORT_OPTIONS: ReadonlyArray<{
  value: TaskHistSortOrder;
  labelKey: string;
}> = [
  { value: TaskHistSortOrder.WorkDate, labelKey: 'archive.sort-work-date' },
  { value: TaskHistSortOrder.UploadDate, labelKey: 'archive.sort-upload-date' },
  { value: TaskHistSortOrder.FileName, labelKey: 'archive.sort-file-name' },
];

export type SortableTaskCard = {
  id: string;
  fileName?: string;
  endTimestamp?: number;
  uploadTimestamp?: number | string;
};

export const FILE_NAME_COLLATOR = new Intl.Collator('pt-BR', {
  sensitivity: 'base',
});

/**
 * Compares two task cards for the given order, matching the order of the API.
 * Cards without upload date or file name (legacy tasks) go last; ties are broken by id.
 */
export function compareTaskCards(
  a: SortableTaskCard,
  b: SortableTaskCard,
  sortOrder: TaskHistSortOrder,
): number {
  if (sortOrder === TaskHistSortOrder.UploadDate) {
    return withIdTieBreak(
      a,
      b,
      compareDefinedFirst(
        toEpochMs(a.uploadTimestamp),
        toEpochMs(b.uploadTimestamp),
        (x, y) => y - x,
      ),
    );
  }
  if (sortOrder === TaskHistSortOrder.FileName) {
    return withIdTieBreak(
      a,
      b,
      compareDefinedFirst(
        toFileName(a.fileName),
        toFileName(b.fileName),
        (x, y) => FILE_NAME_COLLATOR.compare(x, y),
      ),
    );
  }
  return +b.endTimestamp - +a.endTimestamp;
}

function withIdTieBreak(
  a: SortableTaskCard,
  b: SortableTaskCard,
  result: number,
): number {
  if (result !== 0) {
    return result;
  }
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
}

function compareDefinedFirst<V>(
  a: V | undefined,
  b: V | undefined,
  compare: (x: V, y: V) => number,
): number {
  if (a === undefined && b === undefined) {
    return 0;
  }
  if (a === undefined) {
    return 1;
  }
  if (b === undefined) {
    return -1;
  }
  return compare(a, b);
}

function toFileName(value?: string): string | undefined {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return undefined;
}

/** Converts an epoch in milliseconds or a date string to an epoch; anything else is `undefined`. */
export function toEpochMs(value?: number | string): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return undefined;
}
